import { Component } from '@theme/component';

export class ContentCarousel extends Component
{
   static get observedAttributes()
   {
      return [
         'layout-mode',
         'visible-items-mobile',
         'visible-items-tablet',
         'visible-items-desktop',
         'grid-columns-mobile',
         'grid-columns-tablet',
         'grid-columns-desktop',
         'show-dots',
         'show-arrows',
         'dynamic-height',
         'loop',
      ];
   }

   requiredRefs = ['viewport', 'track'];

   #initialized = false;
   #resizeObserver = null;
   #currentPage = 0;
   #abortController = new AbortController();

   connectedCallback()
   {
      if (!this.#initialized)
      {
         this.#build();
         this.#initialized = true;
      }

      super.connectedCallback();

      this.#bindEvents();
      this.#renderDots();
      this.#clampCurrentPage();
      this.#update();
   }

   disconnectedCallback()
   {
      super.disconnectedCallback();
      this.#abortController.abort();
      this.#abortController = new AbortController();
      this.#resizeObserver?.disconnect();
      this.#resizeObserver = null;
   }

   attributeChangedCallback(name, oldValue, newValue)
   {
      if (oldValue === newValue || !this.#initialized) return;

      if (
         name === 'show-dots' ||
         name === 'show-arrows' ||
         name === 'layout-mode' ||
         name === 'visible-items-mobile' ||
         name === 'visible-items-tablet' ||
         name === 'visible-items-desktop'
      )
      {
         this.#renderDots();
         this.#clampCurrentPage();
      }

      this.#update();
   }

   get layoutMode()
   {
      return this.getAttribute('layout-mode') === 'grid' ? 'grid' : 'slider';
   }

   get showDots()
   {
      return this.getAttribute('show-dots') !== 'false';
   }

   get showArrows()
   {
      return this.getAttribute('show-arrows') !== 'false';
   }

   get dynamicHeight()
   {
      return this.getAttribute('dynamic-height') === 'true';
   }

   get loop()
   {
      return this.getAttribute('loop') === 'true';
   }

   get items()
   {
      return this.refs.item || [];
   }

   get isMobile()
   {
      return window.matchMedia('(max-width: 749px)').matches;
   }

   get isTablet()
   {
      return window.matchMedia('(min-width: 750px) and (max-width: 1199px)').matches;
   }

   get visibleItems()
   {
      if (this.isMobile) return this.#parseIntAttr('visible-items-mobile', 1);
      if (this.isTablet) return this.#parseIntAttr('visible-items-tablet', 2);
      return this.#parseIntAttr('visible-items-desktop', 3);
   }

   get gridColumns()
   {
      if (this.isMobile) return this.#parseIntAttr('grid-columns-mobile', 1);
      if (this.isTablet) return this.#parseIntAttr('grid-columns-tablet', 2);
      return this.#parseIntAttr('grid-columns-desktop', 3);
   }

   get stepSize()
   {
      return 1;
   }

   get isStatic()
   {
      return this.layoutMode === 'slider' && this.items.length <= this.visibleItems;
   }

   get pageCount()
   {
      if (!this.items.length) return 1;
      if (this.layoutMode === 'grid' || this.isStatic) return 1;

      const remaining = Math.max(0, this.items.length - this.visibleItems);
      return Math.ceil(remaining / this.stepSize) + 1;
   }

   next()
   {
      this.goTo(this.#currentPage + 1);
   }

   previous()
   {
      this.goTo(this.#currentPage - 1);
   }

   goTo(page)
   {
      if (this.loop)
      {
         if (page < 0) page = this.pageCount - 1;
         if (page >= this.pageCount) page = 0;
      } else
      {
         page = Math.max(0, Math.min(page, this.pageCount - 1));
      }

      if (page === this.#currentPage) return;
      this.#currentPage = page;
      this.#update();
   }

   #build()
   {
      const items = Array.from(this.children).filter(
         (child) => child instanceof HTMLElement && !child.hasAttribute('data-content-carousel-ui')
      );

      const viewport = document.createElement('div');
      viewport.className = 'content-carousel__viewport';
      viewport.setAttribute('ref', 'viewport');

      const track = document.createElement('div');
      track.className = 'content-carousel__track';
      track.setAttribute('ref', 'track');

      for (const item of items)
      {
         item.classList.add('content-carousel__item');
         item.setAttribute('ref', 'item[]');
         track.append(item);
      }

      viewport.append(track);

      const controls = document.createElement('div');
      controls.className = 'content-carousel__controls';
      controls.setAttribute('data-content-carousel-ui', '');
      controls.setAttribute('ref', 'controls');

      const arrows = document.createElement('div');
      arrows.className = 'content-carousel__arrows';
      arrows.setAttribute('data-content-carousel-ui', '');

      const previous = document.createElement('button');
      previous.type = 'button';
      previous.className = 'content-carousel__arrow content-carousel__arrow--previous';
      previous.setAttribute('aria-label', 'Previous items');
      previous.setAttribute('ref', 'previous');
      previous.innerHTML = '<span aria-hidden="true">‹</span>';

      const next = document.createElement('button');
      next.type = 'button';
      next.className = 'content-carousel__arrow content-carousel__arrow--next';
      next.setAttribute('aria-label', 'Next items');
      next.setAttribute('ref', 'next');
      next.innerHTML = '<span aria-hidden="true">›</span>';

      arrows.append(previous, next);

      const dots = document.createElement('div');
      dots.className = 'content-carousel__dots';
      dots.setAttribute('data-content-carousel-ui', '');

      controls.append(arrows, dots);

      this.replaceChildren(viewport, controls);
   }

   #bindEvents()
   {
      const { signal } = this.#abortController;

      this.refs.previous?.addEventListener('click', () => this.previous(), { signal });
      this.refs.next?.addEventListener('click', () => this.next(), { signal });

      window.addEventListener(
         'resize',
         () =>
         {
            this.#clampCurrentPage();
            this.#renderDots();
            this.#update();
         },
         { signal }
      );

      this.#resizeObserver?.disconnect();
      this.#resizeObserver = new ResizeObserver(() => this.#update());
      this.#resizeObserver.observe(this);
      this.#resizeObserver.observe(this.refs.track);

      for (const item of this.items)
      {
         this.#resizeObserver.observe(item);
      }
   }

   #renderDots()
   {
      const dotsContainer = this.querySelector('.content-carousel__dots');
      if (!(dotsContainer instanceof HTMLElement)) return;

      dotsContainer.replaceChildren();

      if (this.layoutMode === 'grid' || this.isStatic || !this.showDots)
      {
         return;
      }

      for (let index = 0; index < this.pageCount; index += 1)
      {
         const dot = document.createElement('button');
         dot.type = 'button';
         dot.className = 'content-carousel__dot';
         dot.setAttribute('aria-label', `Go to page ${index + 1}`);
         dot.toggleAttribute('is-active', index === this.#currentPage);
         dot.addEventListener('click', () => this.goTo(index));
         dotsContainer.append(dot);
      }

      this.updatedCallback();
   }

   #clampCurrentPage()
   {
      this.#currentPage = Math.max(0, Math.min(this.#currentPage, this.pageCount - 1));
   }

   #update()
   {
      const { viewport, track, previous, next, controls } = this.refs;
      if (!(viewport instanceof HTMLElement) || !(track instanceof HTMLElement) || !this.items.length) return;

      this.style.setProperty('--content-carousel-grid-columns', `${this.gridColumns}`);
      this.style.setProperty(
         '--content-carousel-item-width',
         `calc((100% - (var(--content-carousel-gap, 16px) * ${Math.max(this.visibleItems - 1, 0)})) / ${this.visibleItems})`
      );

      const shouldShowControls = this.layoutMode === 'slider' && !this.isStatic && (this.showArrows || this.showDots);
      if (controls instanceof HTMLElement)
      {
         controls.hidden = !shouldShowControls;
      }

      const arrowsContainer = this.querySelector('.content-carousel__arrows');
      if (arrowsContainer instanceof HTMLElement)
      {
         arrowsContainer.hidden = this.layoutMode !== 'slider' || this.isStatic || !this.showArrows;
      }

      const dotsContainer = this.querySelector('.content-carousel__dots');
      if (dotsContainer instanceof HTMLElement)
      {
         dotsContainer.hidden = this.layoutMode !== 'slider' || this.isStatic || !this.showDots;
      }

      if (this.layoutMode === 'grid')
      {
         track.style.transform = 'none';
         viewport.style.height = 'auto';
         if (previous instanceof HTMLButtonElement) previous.disabled = true;
         if (next instanceof HTMLButtonElement) next.disabled = true;
         return;
      }

      if (this.isStatic)
      {
         track.style.transform = 'none';
         viewport.style.height = 'auto';
         if (previous instanceof HTMLButtonElement) previous.disabled = true;
         if (next instanceof HTMLButtonElement) next.disabled = true;
         return;
      }

      const startIndex = this.#currentPage * this.stepSize;
      const startItem = this.items[startIndex];
      if (!(startItem instanceof HTMLElement)) return;

      const offset = startItem.offsetLeft;
      track.style.transform = `translate3d(${-offset}px, 0, 0)`;

      if (this.dynamicHeight)
      {
         const endIndex = Math.min(startIndex + this.visibleItems - 1, this.items.length - 1);
         const visibleItems = this.items.slice(startIndex, endIndex + 1);
         const height = visibleItems.reduce((maxHeight, item) => Math.max(maxHeight, item.offsetHeight), 0);
         viewport.style.height = `${height}px`;
      } else
      {
         viewport.style.height = 'auto';
      }

      if (previous instanceof HTMLButtonElement)
      {
         previous.disabled = !this.loop && this.#currentPage === 0;
      }

      if (next instanceof HTMLButtonElement)
      {
         next.disabled = !this.loop && this.#currentPage >= this.pageCount - 1;
      }

      const dots = this.querySelectorAll('.content-carousel__dot');
      dots.forEach((dot, index) =>
      {
         dot.toggleAttribute('is-active', index === this.#currentPage);
      });
   }

   #parseIntAttr(name, fallback)
   {
      const value = Number.parseInt(this.getAttribute(name) || '', 10);
      return Number.isNaN(value) ? fallback : Math.max(1, value);
   }
}

if (!customElements.get('content-carousel-component'))
{
   customElements.define('content-carousel-component', ContentCarousel);
}
